import { useQuery } from "@tanstack/react-query";
import { Completion } from "../types/Completion";
import { useMission } from "./mission-queries";
import { calcStreakLength } from "../util/Streak";
import { fullDaysBetween } from "../util/Time";
import { BackendAPI } from "../util/Env";
import { useMsal } from "@azure/msal-react";

export const completionsKey = 'completions';

export const useCompletionsQuery = () => {
    const { accounts } = useMsal();
    const { data } = useQuery({
        queryKey: [completionsKey],
        queryFn: async () => {
            const response = await fetch(`${BackendAPI}/completions-api.php`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'ownerId': accounts[0].idTokenClaims!.oid!,
                },
            });

            if (!response.ok) {
                throw new Error(`Error fetching completions: ${response.statusText}`);
            }

            return (await response.json()) as Completion[];
        }
    })

    if (data) {
        data.forEach(completion => {
            if (typeof completion.date === 'string') {
                completion.date = new Date(completion.date);
            }

            if ((completion as any).mission_id !== undefined) {
                completion.missionId = (completion as any).mission_id;
            }
        });
        data.sort((a, b) => b.date.getTime() - a.date.getTime());
    }

    return { completions: data }
}

export const useCompletionsQueryByMission = (missionId: string | undefined) => {
    const { completions } = useCompletionsQuery()

    if (!missionId) {
        return { completions: [], count: 0 }
    }

    const missionCompletions = completions?.filter(completion => completion.missionId === missionId)
    return { completions: missionCompletions, count: missionCompletions?.length }
}

export const useCoins = () => {
    const { completions } = useCompletionsQuery()

    if (!completions) {
        return { coins: 0 }
    }

    const coins = completions.reduce((acc, completion) => acc + completion.coins, 0)
    return { coins }
}

export const useStats = () => {
    const { completions } = useCompletionsQuery()

    if (!completions) {
        return { stats: {} }
    }

    const stats = completions.reduce((acc, completion) => {
        if (!acc[completion.category]) {
            acc[completion.category] = 0;
        }
        acc[completion.category] += completion.coins;
        return acc;
    }, {} as Record<string, number>);

    return { stats }
}

export const useStreak = (missionId: string | undefined) => {
    const { mission } = useMission(missionId);
    const { completions } = useCompletionsQueryByMission(missionId);

    if (!mission || !completions || completions.length <= 0 || mission.streakExpiration === undefined) {
        return { streakExpired: undefined, remainingDays: undefined, streakLength: undefined, streakLvl: undefined, available: false }
    }

    const now = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 0);

    let streakLvl: number = 0;
    let bonusPerC = 0;

    const lastCompletion = completions.sort((a, b) => b.date.getTime() - a.date.getTime())[0];
    const remainingDays = mission.streakExpiration - fullDaysBetween(lastCompletion.date, now);
    const streakExpired = remainingDays < 0;
    const streakLength = calcStreakLength(mission.streakExpiration)([...completions.map(c => c.date), now]);

    if (streakLength >= 50) {
        streakLvl = 3
        bonusPerC = 30
    } else if (streakLength >= 10) {
        streakLvl = 2
        bonusPerC = 20
    } else if (streakLength >= 3) {
        streakLvl = 1
        bonusPerC = 10
    }

    return { streakExpired, remainingDays, streakLength, streakLvl, bonusPerC, available: true }
}